import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CursorPagination, useCursorPager } from '@nucleus-commerce/admin-kit/data-table';
import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { DiscountCode } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';

import { CODES_LIMIT, useDiscountCodes } from '../hooks/use-discount-codes';
import { useUpdateDiscountCode } from '../hooks/use-discount-mutations';
import { CODE_STATES, codeStateFilter, type CodeState } from '../schemas/discount-rules';

import { DeleteCodeDialog } from './delete-code-dialog';

const MAX_QUERY = 40;

export interface DiscountCodeListProps {
  storeId: string;
  discountId: string;
  canWrite: boolean;
}

/** Every code of the discount, a page at a time, searchable and filterable by state. */
export function DiscountCodeList({ storeId, discountId, canWrite }: DiscountCodeListProps) {
  const { t } = useTranslation('discounts');
  const format = useFormat();
  const message = useErrorMessage();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [state, setState] = useState<CodeState>('all');
  const [cursor, setCursor] = useState<string | undefined>();
  const [deleting, setDeleting] = useState<DiscountCode | null>(null);
  const toggle = useUpdateDiscountCode(storeId, discountId);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(q.trim().slice(0, MAX_QUERY)), 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  const resetKey = `${discountId}|${debounced}|${state}`;
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (lastResetKey !== resetKey) {
    setLastResetKey(resetKey);
    setCursor(undefined);
  }
  const pager = useCursorPager({ cursor, onNavigate: setCursor, resetKey });
  const query = useDiscountCodes(storeId, discountId, {
    q: debounced,
    isActive: codeStateFilter(state),
    cursor,
  });
  const codes = query.data?.items ?? [];

  const setActive = (code: DiscountCode, isActive: boolean) =>
    toggle.mutate(
      { codeId: code.id, isActive },
      {
        onSuccess: () =>
          toast.success(
            t(isActive ? 'codes.toasts.enabled' : 'codes.toasts.disabled', { code: code.code }),
          ),
        onError: (error) => toast.error(message(error)),
      },
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          maxLength={MAX_QUERY}
          placeholder={t('codes.search')}
          aria-label={t('codes.search')}
          className="max-w-xs"
        />
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={state}
          aria-label={t('codes.stateFilter')}
          onValueChange={(value) => {
            const next = CODE_STATES.find((option) => option === value);
            if (next) setState(next);
          }}
        >
          {CODE_STATES.map((option) => (
            <ToggleGroupItem key={option} value={option}>
              {t(`codes.states.${option}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      {query.isPending ? (
        <Skeleton className="h-24" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : codes.length === 0 && !cursor ? (
        <p className="text-muted-foreground text-sm">
          {debounced || state !== 'all' ? t('codes.noMatches') : t('codes.empty')}
        </p>
      ) : (
        <>
          <Table aria-label={t('codes.list')}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('codes.code')}</TableHead>
                <TableHead className="text-right">{t('codes.usesColumn')}</TableHead>
                <TableHead>{t('codes.state')}</TableHead>
                <TableHead>{t('codes.created')}</TableHead>
                {canWrite ? (
                  <TableHead>
                    <span className="sr-only">{t('codes.actions')}</span>
                  </TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell>
                    <code className="text-[13px]">{code.code}</code>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{code.usageCount}</TableCell>
                  <TableCell>
                    {code.isActive ? (
                      <Badge variant="secondary">{t('codes.states.active')}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        {t('codes.states.inactive')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground" title={format.utc(code.createdAt)}>
                    {format.relative(code.createdAt)}
                  </TableCell>
                  {canWrite ? (
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Switch
                          checked={code.isActive}
                          disabled={toggle.isPending && toggle.variables.codeId === code.id}
                          onCheckedChange={(checked) => setActive(code, checked)}
                          aria-label={t('codes.toggle', { code: code.code })}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t('codes.deleteOne', { code: code.code })}
                          onClick={() => setDeleting(code)}
                        >
                          <Trash2 aria-hidden />
                        </Button>
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <CursorPagination
            pager={pager}
            nextCursor={query.data.nextCursor}
            limit={CODES_LIMIT}
            isFetching={query.isFetching}
            count={codes.length}
          />
        </>
      )}
      {canWrite ? (
        <DeleteCodeDialog
          storeId={storeId}
          discountId={discountId}
          code={deleting}
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
        />
      ) : null}
    </div>
  );
}
